'use client';

// Ties the anonymous browser identity to the signed-in user on load and sets
// the person properties PostHog cohorts/filters use (plan, credits, etc.).
// Reads the same /api/auth/me the wallet reads — which already returns id +
// email + is_premium + credit_balance + has_subscription, so identity needs no
// backend change. Renders nothing.
//
// Identity merge: posthog-js starts each visitor anonymous; identify() links
// that anonymous person — and their pre-signup landing/signup events — to the
// user, which is what makes the visitor → signup funnel whole. Server-side
// events use distinct_id = String(user_id) so they land on the same person.

import { useEffect } from 'react';
import { identifyUser, resetUser } from '@/lib/analytics';
import { fetchMe } from '@/lib/session/sessionSummary';

export default function AnalyticsIdentity() {
  useEffect(() => {
    let cancelled = false;
    // Shared with the marketing header's account links: one read per load.
    fetchMe()
      .then((d) => {
        if (cancelled) return;
        if (d && d.id != null) {
          identifyUser(String(d.id), {
            email: d.email,
            email_verified: !!d.email_verified,
            is_premium: !!d.is_premium,
            credit_balance: d.credit_balance ?? 0,
            has_subscription: !!d.has_subscription,
            prefers_metric: d.prefers_metric ?? 0,
          });
        } else {
          // Logged out (401 / no id): drop any identity left by a previous user
          // on this browser. resetUser() self-guards so an always-anonymous
          // visitor keeps a stable id across loads (funnel continuity).
          resetUser();
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
